import React, { useEffect, useState } from 'react';
import {
	FingotiButton,
	FingotiModal,
	FingotiDelete,
	FingotiHeader,
	FingotiMarkdown,
	FingotiTable,
} from '@fingoti/components';
import { useFingotiTokens, useFingotiAuth } from '@fingoti/auth-js/react';
import { useSnackbar } from 'notistack';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';

import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import PersonIcon from '@material-ui/icons/Person';

import { datetimeService } from '../../services/datetime.service';
import { useProfileState } from '../../context/ProfileContext';

import { UserTokenAdd } from './UserTokenAdd';
import { UserTokenEdit } from './UserTokenEdit';
import { UserTokenInfo } from './UserTokenInfo';
import { UserTokenReveal } from './UserTokenReveal';
import { useTheme } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(6),
		width: '100%',
	},
	actionIcon: {
		padding: 0,
	},
	enabledIcon: {
		verticalAlign: 'middle',
		fill: theme.palette.success.main,
	},
	disabledIcon: {
		verticalAlign: 'middle',
		fill: theme.palette.error.main,
	},
	avatar: {
		fontSize: '1.1rem',
		border: '2px solid #E0E0E0',
		marginRight: theme.spacing(2),
	},
	organisationWrapper: {
		display: 'flex',
		alignItems: 'center',
	},
	expired: {
		color: '#FF0000',
	},
}));

export const UserTokens = () => {
	const theme = useTheme();
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const {
		tokens,
		isLoading,
		error,
		lastCreated,
		create,
		update,
		remove,
		clearLastCreated,
	} = useFingotiTokens();
	const { session, isActingAs } = useFingotiAuth();
	const { organisations } = useProfileState();
	const [addOpen, setAddOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);
	const [editToken, setEditToken] = useState();
	const [infoToken, setInfoToken] = useState();

	// Surface a list/mutation load error once when it changes.
	useEffect(() => {
		if (error) {
			enqueueSnackbar(error?.message ?? 'Failed to load tokens', {
				variant: 'error',
			});
		}
	}, [error, enqueueSnackbar]);

	const organisationName = (id) =>
		organisations?.find((org) => org.id === id)?.organisationName ?? id;

	const deleteToken = async (id) => {
		try {
			await remove(id);
			enqueueSnackbar('Token deleted', { variant: 'success' });
		} catch (err) {
			enqueueSnackbar(err?.message ?? 'Failed to delete token', {
				variant: 'error',
			});
		}
	};

	const organisationNameRenderer = (rd) => {
		return (
			<div className={classes.organisationWrapper}>
				<Avatar
					className={classes.avatar}
					size='small'
					src={`https://cdn.fingoti.com/images/avatars/${rd.organisationId}`}>
					<AccountBalanceOutlinedIcon />
				</Avatar>
				{organisationName(rd.organisationId)}
			</div>
		);
	};

	const enabledRenderer = (rd) => {
		return rd.disabled ? (
			<BlockOutlinedIcon className={classes.disabledIcon} />
		) : (
			<CheckCircleOutlineIcon className={classes.enabledIcon} />
		);
	};

	const scopesRenderer = (rd) => {
		const scopes = rd.scopes ?? [];
		return (
			<Tooltip title={scopes.join(', ') || 'None'}>
				<span>{`${scopes.length} scope${scopes.length === 1 ? '' : 's'}`}</span>
			</Tooltip>
		);
	};

	const expiryRenderer = (rd) => {
		if (rd.expiresOn === null) {
			return 'Never';
		}
		const expDate = new Date(rd.expiresOn);
		return expDate > new Date() ? (
			datetimeService.formatDateTimeNoSeconds(expDate)
		) : (
			<span className={classes.expired}>
				{datetimeService.formatDateTimeNoSeconds(expDate)}
			</span>
		);
	};

	const handleNewToken = () => {
		// The hook surfaces the one-time plaintext via lastCreated, which opens
		// the reveal modal; just close the add form here.
		setAddOpen(false);
	};

	const handleEditToken = () => {
		setEditOpen(false);
	};

	const handleInfoOpen = (token) => {
		setInfoToken(token);
		setInfoOpen(true);
	};

	const handleEditOpen = (token) => {
		setEditToken(token);
		setEditOpen(true);
	};

	const actionButtons = () => {
		return (
			<React.Fragment>
				<Tooltip
					title={
						isActingAs
							? 'Exit the tenant before creating a token'
							: ''
					}>
					<span>
						<FingotiButton
							light
							color='primary'
							disabled={isActingAs}
							onClick={() => setAddOpen(true)}>
							new token
						</FingotiButton>
					</span>
				</Tooltip>
			</React.Fragment>
		);
	};

	const breadcrumbs = [
		{ text: 'User', link: '/user' },
		{ text: 'Tokens', link: '' },
	];

	return (
		<React.Fragment>
			<FingotiModal title='Add Token' open={addOpen} setOpen={setAddOpen}>
				<UserTokenAdd
					create={create}
					availableScopes={session?.scopes ?? []}
					handleNewToken={handleNewToken}
					setOpen={setAddOpen}
				/>
			</FingotiModal>
			<FingotiModal title='Edit Token' open={editOpen} setOpen={setEditOpen}>
				<UserTokenEdit
					update={update}
					handleEditToken={handleEditToken}
					token={editToken}
					setOpen={setEditOpen}
				/>
			</FingotiModal>
			<FingotiModal
				title='Token Information'
				open={infoOpen}
				setOpen={setInfoOpen}>
				<UserTokenInfo
					setOpen={setInfoOpen}
					token={infoToken}
					organisationName={organisationName}
				/>
			</FingotiModal>
			<FingotiModal
				title='Token Created'
				open={Boolean(lastCreated)}
				setOpen={(open) => {
					if (!open) clearLastCreated();
				}}>
				<UserTokenReveal token={lastCreated} onDone={clearLastCreated} />
			</FingotiModal>
			<FingotiHeader
				breadcrumbs={breadcrumbs}
				actionButtons={actionButtons()}
				sectionIcon={PersonIcon}
			/>
			<FingotiMarkdown path='/documentation/account/user/tokens.md' />
			<FingotiTable
				data={tokens}
				isLoading={isLoading}
				columns={[
					{ title: 'id', field: 'id', hidden: true },
					{
						title: 'organisation',
						field: 'organisationId',
						cellStyle: { padding: theme.spacing(1) },
						render: organisationNameRenderer,
					},
					{
						title: 'enabled',
						field: 'disabled',
						filtering: false,
						align: 'center',
						cellStyle: { width: '8%' },
						render: enabledRenderer,
					},
					{ title: 'name', field: 'name' },
					{
						title: 'scopes',
						filtering: false,
						render: scopesRenderer,
					},
					{
						title: 'expiry',
						field: 'expiresOn',
						filtering: false,
						render: expiryRenderer,
					},
				]}
				actions={[
					(rowData) => ({
						icon: InfoOutlinedIcon,
						tooltip: 'token info',
						onClick: () => handleInfoOpen(rowData),
					}),
					(rowData) => ({
						icon: EditOutlinedIcon,
						tooltip: 'edit token',
						onClick: () => handleEditOpen(rowData),
					}),
					(rowData) => ({
						icon: () => (
							<FingotiDelete
								tooltipText='delete token'
								className={classes.actionIcon}
								onClick={() => deleteToken(rowData.id)}
							/>
						),
					}),
				]}
			/>
		</React.Fragment>
	);
};
